/**
 * HOCP Operational Design System — Showcase Page
 * ────────────────────────────────────────────────────────────────────
 * Live reference of every operational primitive. Used by engineers as
 * source-of-truth and by testers to verify touch targets + a11y.
 *
 * Route: /design-system/operational (dev-only convenience)
 */
import { useState } from "react";
import {
  Bed, ChefHat, Wrench, Coffee, Timer, Package, Users2, DollarSign,
  Flame, ReceiptText, Ban, Send, Plus, LogIn, LogOut,
  UserPlus, CalendarPlus, ArrowLeftRight, CalendarClock, Search,
  Printer, CreditCard,
} from "lucide-react";
import {
  OperationalButton, OperationalCard, StatusChip, PrimaryActionBar,
  QuickActionTile, TaskCard, ConfirmSheet, OperationalDrawer,
  BottomActionBar, LargeSearchBox, STATUS_TOKENS,
  GuestWorkspace, RoomOperationalCard, FloorRoomPicker,
  GuestSearchPanel, TodaysWorkPanel, QuickActionGrid,
} from "@/components/operational";

export default function OperationalDemo() {
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="ods-demo">
      <div className="max-w-6xl mx-auto space-y-10">

        {/* Header — uses PrimaryActionBar */}
        <PrimaryActionBar
          title="Operational Design System"
          subtitle="Every operational module builds from these primitives."
          testid="ods-header"
          actions={[
            { label: "New Task", icon: Plus, tone: "primary", size: "standard",
              onClick: () => alert("New Task"), testid: "ods-header-new" },
          ]}
        />

        <Section title="1 · OperationalButton" description="Tones: primary · secondary · success · warning · danger. Sizes: primary (56 px), standard (48 px), secondary (44 px). Always uses `label`, never children.">
          <div className="flex flex-wrap gap-3">
            <OperationalButton label="Primary Action"    tone="primary"   icon={Send}     testid="btn-primary" />
            <OperationalButton label="Secondary"         tone="secondary" icon={LogIn}    testid="btn-secondary" />
            <OperationalButton label="Success"           tone="success"   icon={Flame}    testid="btn-success" />
            <OperationalButton label="Warning"           tone="warning"   icon={Timer}    testid="btn-warning" />
            <OperationalButton label="Danger"            tone="danger"    icon={Ban}      testid="btn-danger" />
            <OperationalButton label="Loading…"          tone="primary"   loading         testid="btn-loading" />
            <OperationalButton label="Disabled"          tone="primary"   disabled        testid="btn-disabled" />
            <OperationalButton label="Full-size" sublabel="56 px height for tablets"
                                tone="primary" size="primary" icon={ChefHat} testid="btn-full-size"/>
          </div>
        </Section>

        <Section title="2 · OperationalCard" description="20 px padding, 12 px radius, 2 px border. Interactive when `onClick` is provided.">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <OperationalCard testid="card-neutral">
              <div className="font-semibold text-[15px]">Static card</div>
              <div className="text-[13px] text-slate-500 mt-1">Read-only content block</div>
            </OperationalCard>
            <OperationalCard testid="card-interactive"
              onClick={() => alert("interactive")}
              tone="info">
              <div className="font-semibold text-[15px]">Interactive card</div>
              <div className="text-[13px] text-slate-500 mt-1">Hover + click affordance</div>
            </OperationalCard>
            <OperationalCard testid="card-warning" tone="warning">
              <div className="font-semibold text-[15px]">Warning card</div>
              <div className="text-[13px] text-slate-500 mt-1">For heads-up messages</div>
            </OperationalCard>
          </div>
        </Section>

        <Section title="3 · StatusChip" description="Canonical 7-state platform language. Icon + text + colour on every chip.">
          <div className="flex flex-wrap gap-2">
            {Object.keys(STATUS_TOKENS).map(k => (
              <StatusChip key={k} status={k} size="lg"
                            testid={`chip-${k}-lg`}/>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.keys(STATUS_TOKENS).map(k => (
              <StatusChip key={k} status={k} size="md"/>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {Object.keys(STATUS_TOKENS).map(k => (
              <StatusChip key={k} status={k} size="sm"/>
            ))}
          </div>
        </Section>

        <Section title="5 · QuickActionTile" description="Big tap-target with count. 112 px min-height. Ideal for dashboard KPIs.">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <QuickActionTile label="Rooms Ready"   count={42} icon={Bed}   status="ready"    testid="tile-ready"/>
            <QuickActionTile label="Occupied"      count={18} icon={Users2} status="active"   testid="tile-active"/>
            <QuickActionTile label="Cleaning"      count={7}  icon={Timer}  status="pending"  testid="tile-pending"/>
            <QuickActionTile label="Delayed"       count={2}  icon={Wrench} status="attention" testid="tile-attention"/>
            <QuickActionTile label="Out of Order"  count={1}  icon={Ban}    status="critical" testid="tile-critical"/>
            <QuickActionTile label="Blocked"       count={0}  icon={Ban}    status="blocked"  testid="tile-blocked"/>
            <QuickActionTile label="Unknown"       count={0}  icon={Package} status="unknown" testid="tile-unknown"/>
          </div>
        </Section>

        <Section title="6 · TaskCard" description="Canonical shape for any operational work item.">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TaskCard
              testid="task-1"
              title="Room 305 · Cleaning"
              sublabel="Housekeeping · Standard"
              status="active"
              meta={[
                { icon: Timer, label: "12 min elapsed" },
                { icon: Users2, label: "Anita R." },
              ]}
              primaryAction={{ label: "Mark Complete", icon: Flame, tone: "success",
                                  onClick: () => alert("done") }}
            />
            <TaskCard
              testid="task-2"
              title="AC not cooling · Room 210"
              sublabel="Engineering · Urgent"
              status="attention"
              meta={[
                { icon: Timer, label: "42 min open" },
                { icon: Wrench, label: "Assigned: Ravi K." },
              ]}
              primaryAction={{ label: "Start Work", icon: Wrench, tone: "primary",
                                  onClick: () => alert("start") }}
            />
          </div>
        </Section>

        <Section title="7 · ConfirmSheet" description="Bottom sheet for destructive confirmations. Prefer undo-toast where reversible.">
          <OperationalButton label="Open Confirm Sheet" icon={Ban} tone="danger"
                              size="standard"
                              onClick={() => setConfirmOpen(true)}
                              testid="btn-open-confirm"/>
          <ConfirmSheet
            open={confirmOpen}
            onOpenChange={setConfirmOpen}
            title="Void this order?"
            body="This will release the table and cannot be undone."
            confirmLabel="Yes, void"
            cancelLabel="Keep order"
            confirmTone="danger"
            loading={loading}
            onConfirm={async () => {
              setLoading(true);
              await new Promise(r => setTimeout(r, 500));
              setLoading(false); setConfirmOpen(false);
            }}
            testid="demo-confirm"
          />
        </Section>

        <Section title="8 · OperationalDrawer" description="Right slide-in for detail workflows. Header + scrollable body + optional footer.">
          <OperationalButton label="Open Drawer" icon={LogIn} tone="primary"
                              size="standard"
                              onClick={() => setDrawerOpen(true)}
                              testid="btn-open-drawer"/>
          <OperationalDrawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            title="Order R-1024"
            subtitle="Dine-in · Table T-12 · 3 guests"
            statusChip={<StatusChip status="active" label="In Kitchen" size="md"/>}
            testid="demo-drawer"
            footer={
              <BottomActionBar
                primary={{ label: "Fire to Kitchen", icon: Flame, tone: "warning",
                            onClick: () => alert("fire"), testid: "demo-drawer-fire" }}
                secondaryActions={[
                  { label: "Void", icon: Ban, tone: "danger",
                    onClick: () => alert("void"), testid: "demo-drawer-void" },
                ]}
              />
            }
          >
            <div className="p-5 space-y-3">
              <div className="text-[15px] text-slate-600">Line items and menu browser go here.</div>
              <OperationalCard testid="drawer-inner-card">
                <div className="text-[14px]">Masala Dosa × 2 — ₹500</div>
              </OperationalCard>
            </div>
          </OperationalDrawer>
        </Section>

        <Section title="9 · BottomActionBar" description="Sticky footer for detail screens. One primary CTA + up to 3 secondary actions.">
          <OperationalCard padded={false} testid="bar-demo-card">
            <div className="p-4 text-[14px] text-slate-500 min-h-[80px]">
              (Body content of a detail screen)
            </div>
            <BottomActionBar
              primary={{ label: "Save Changes", icon: ReceiptText, tone: "success",
                          onClick: () => alert("save"), testid: "bar-save" }}
              secondaryActions={[
                { label: "Discard", icon: Ban, tone: "secondary",
                  onClick: () => alert("discard"), testid: "bar-discard" },
              ]}
              helper="Auto-saved 2 min ago"
              testid="bar-demo"
            />
          </OperationalCard>
        </Section>

        <Section title="10 · LargeSearchBox" description="48 px height search with clear button. Submit on Enter.">
          <div className="max-w-lg">
            <LargeSearchBox
              value={search}
              onChange={setSearch}
              onSubmit={(v) => alert("search: " + v)}
              placeholder="Search rooms, orders, guests..."
              testid="demo-search"
            />
          </div>
        </Section>

        <Section title="Combined example" description="Everything working together — a plausible operational screen.">
          <OperationalCard padded={false} testid="combined">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3">
              <div>
                <div className="font-bold text-[18px]">Housekeeping · 2nd Floor</div>
                <div className="text-[13px] text-slate-500">27 rooms · Shift A</div>
              </div>
              <StatusChip status="active" label="Live" size="md"/>
            </div>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <QuickActionTile label="Ready"    count={12} icon={Bed}    status="ready"     testid="c-ready"/>
              <QuickActionTile label="Cleaning" count={7}  icon={Timer}  status="pending"   testid="c-pending"/>
              <QuickActionTile label="Inspect"  count={3}  icon={Wrench} status="attention" testid="c-attention"/>
              <QuickActionTile label="OOO"      count={1}  icon={Ban}    status="critical"  testid="c-critical"/>
            </div>
          </OperationalCard>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-[20px] font-bold">{title}</h2>
        {description && (
          <p className="text-[13px] text-slate-500 max-w-3xl mt-0.5">{description}</p>
        )}
      </div>
      <div className="pt-1">{children}</div>
    </section>
  );
}
